/**
 * useStructuredGrowth
 *
 * Primary hook for the Structured Growth Engine.
 * Provides real-time settings + group health for any entity.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { saveGrowthSettings, computeGroupHealth } from './structuredGrowthService';
import type {
  StructuredGrowthSettings,
  GroupHealthStats,
  StructuredGrowthEntityType,
} from './types';

interface UseStructuredGrowthResult {
  settings: StructuredGrowthSettings | null;
  health: GroupHealthStats | null;
  loading: boolean;
  error: string | null;
  refreshHealth: () => Promise<void>;
  updateSettings: (
    patch: Partial<Omit<StructuredGrowthSettings, 'entityId' | 'updatedAt'>>,
    updatedBy: string,
  ) => Promise<void>;
}

export function useStructuredGrowth(
  entityId: string | null | undefined,
  entityType: StructuredGrowthEntityType,
): UseStructuredGrowthResult {
  const [settings, setSettings] = useState<StructuredGrowthSettings | null>(null);
  const [health, setHealth] = useState<GroupHealthStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref keeps latest settings available inside callbacks without stale closure
  const settingsRef = useRef<StructuredGrowthSettings | null>(null);
  settingsRef.current = settings;

  // Real-time settings listener
  useEffect(() => {
    if (!entityId) {
      setSettings(null);
      return;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(db, 'structuredGrowthSettings', entityId),
      (snap) => {
        setSettings(
          snap.exists()
            ? ({ entityId, ...snap.data() } as StructuredGrowthSettings)
            : null,
        );
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [entityId]);

  const refreshHealth = useCallback(async () => {
    if (!entityId) return;
    try {
      const stats = await computeGroupHealth(entityId);
      setHealth(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fout bij laden gezondheidsdata');
    }
  }, [entityId]);

  // Load health once on mount / entity change
  useEffect(() => {
    if (entityId) refreshHealth();
  }, [entityId, refreshHealth]);

  const updateSettings = useCallback(
    async (
      patch: Partial<Omit<StructuredGrowthSettings, 'entityId' | 'updatedAt'>>,
      updatedBy: string,
    ) => {
      if (!entityId) return;
      const current = settingsRef.current;
      try {
        await saveGrowthSettings({
          entityId,
          entityType: current?.entityType ?? entityType,
          mainGoal: current?.mainGoal ?? '',
          cycleType: current?.cycleType ?? 'open',
          cycleStartDate: current?.cycleStartDate ?? null,
          sessionStructure: current?.sessionStructure ?? [],
          groupRules: current?.groupRules ?? [],
          requirePreSession: current?.requirePreSession ?? false,
          requirePostSession: current?.requirePostSession ?? true,
          updatedBy,
          ...patch,
        });
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Fout bij opslaan instellingen',
        );
        throw err;
      }
    },
    [entityId, entityType],
  );

  return { settings, health, loading, error, refreshHealth, updateSettings };
}
